import { ListItemIcon, type MenuItemProps, Tooltip } from '@mui/material';
import {
  AddPhotoAlternateOutlined as AddImageIcon,
  Warning as ErrorIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';
import MenuItemLoadingIndicator from '@/components/Menu/MenuItemLoadingIndicator';
import { UploadError } from './useUploadThumbnail';
import UploadMenuItem from '@/components/FileAttachments/AddFiles/AddFilesControls/utils/upload/UploadMenuItem';
import UploadMenuItemContent from '@/components/FileAttachments/AddFiles/AddFilesControls/utils/upload/UploadMenuItem/UploadMenuItemContent';
import TonalIconButton from '@/components/TonalIconButton';
import { FormattedMessage } from 'react-intl';
import FormattedHtmlMessage from '@/components/FormattedHtmlMessage';

type UploadThumbnailMenuItemProps = Omit<
  MenuItemProps,
  'onClick' | 'disabled' | 'children'
> & {
  variant: 'add' | 'edit';
  onOpenFileSelector: () => void;
  uploading: boolean;
  uploadError: UploadError | null;
  onDismissUploadError: () => void;
};

const UploadThumbnailMenuItem = ({
  variant,
  onOpenFileSelector,
  uploading,
  uploadError,
  onDismissUploadError,
  ...props
}: UploadThumbnailMenuItemProps) => (
  <UploadMenuItem
    onClick={uploading || uploadError !== null ? undefined : onOpenFileSelector}
    disabled={uploading}
    disableRipple={uploadError !== null}
    {...props}
  >
    <ListItemIcon>
      {uploading ? (
        <MenuItemLoadingIndicator />
      ) : uploadError !== null ? (
        <ErrorIcon color="warning" />
      ) : (
        <AddImageIcon />
      )}
    </ListItemIcon>
    <UploadMenuItemContent>
      {uploading ? (
        <FormattedMessage id="portfolio.challenge.upload_thumbnail.menu_item.uploading_state" />
      ) : uploadError !== null ? (
        <>
          <span>
            <FormattedHtmlMessage
              id={`portfolio.challenge.upload_thumbnail.menu_item.error_state.${uploadError === UploadError.FILE_TOO_LARGE ? 'file_too_large' : 'generic'}.label`}
            />
          </span>
          <Tooltip
            title={
              <FormattedMessage
                id={`portfolio.challenge.upload_thumbnail.menu_item.error_state.${uploadError === UploadError.FILE_TOO_LARGE ? 'file_too_large' : 'generic'}.retry_button`}
              />
            }
          >
            <TonalIconButton size="small" onClick={onDismissUploadError}>
              <RetryIcon fontSize="small" />
            </TonalIconButton>
          </Tooltip>
        </>
      ) : (
        <FormattedMessage
          id={`portfolio.challenge.upload_thumbnail.menu_item.label.${variant}`}
        />
      )}
    </UploadMenuItemContent>
  </UploadMenuItem>
);

export default UploadThumbnailMenuItem;
